<template>
  <div id="pdf-content" ref="pdfRef">
    <header class="pdf-header">
      <div>
        <h1>Beställning av betongbrunnar</h1>
        <div class="subtitle">
          Skicka ditt underlag till
          <a class="link" href="mailto:offert@benders.se">offert@benders.se</a> för att få en
          offert, eller till <a class="link" href="mailto:order@benders.se">order@benders.se</a> för
          att genomföra en beställning
        </div>
      </div>
      <img class="logo" :src="bendersBlob" />
    </header>

    <table class="customer-info">
      <tbody>
        <!-- Två fält per rad; sista raden blir kortare vid udda antal. -->
        <tr v-for="(row, r) in customerRows" :key="r">
          <td v-for="f in row" :key="f.key">
            <strong>{{ f.label }}:</strong><span>{{ f.value(source) }}</span>
          </td>
        </tr>
        <template v-if="customer.information">
          <tr>
            <td style="padding-top: 1rem"><strong>Övrig information: </strong></td>
          </tr>
          <tr>
            <td>{{ customer.information }}</td>
          </tr>
        </template>
      </tbody>
    </table>

    <section
      v-for="(form, index) in forms"
      :key="index"
      class="brunn-section"
      style="break-inside: avoid; page-break-inside: avoid"
    >
      <h2>Brunn {{ index + 1 }}</h2>
      <div>
        <table>
          <tbody>
            <tr v-for="(f, fi) in wellFields" :key="f.key">
              <td>
                <strong>{{ f.label }}:</strong> <span>{{ f.value(form) }}</span>
              </td>
              <!-- Urtavlan spänner över hela fältlistan, i första raden. -->
              <td v-if="fi === 0" id="dialCell" :rowspan="wellFields.length">
                <div class="dial-pdf-wrapper">
                  <img :src="dialBlob" />
                  <svg
                    v-if="inletArrows(form).length"
                    class="dial-pdf-overlay"
                    viewBox="-20 -20 321 323"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <!-- Färg sätts som presentationsattribut, inte via CSS: den externa
                         PDF-renderaren applicerar dokumentets CSS på HTML men kaskaderar den inte
                         in i inline-SVG, så klassade element blev svarta (svart siffra på svart
                         bricka). dy i stället för dominant-baseline av samma skäl – renderaren
                         stödjer inte dominant-baseline. -->
                    <g v-for="a in inletArrows(form)" :key="a.idx">
                      <line
                        :x1="a.x1"
                        :y1="a.y1"
                        :x2="a.x2"
                        :y2="a.y2"
                        stroke="#004f9f"
                        stroke-width="2"
                      />
                      <polygon :points="a.head" fill="#004f9f" />
                      <circle :cx="a.lx" :cy="a.ly" r="11" fill="#004f9f" />
                      <text
                        :x="a.lx"
                        :y="a.ly"
                        text-anchor="middle"
                        dy="0.35em"
                        fill="#ffffff"
                        font-size="15"
                        font-weight="700"
                        font-family="Inter, Arial, sans-serif"
                      >
                        {{ a.idx }}
                      </text>
                    </g>
                  </svg>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <table class="anslutningar">
        <thead>
          <tr>
            <td v-for="c in connectionColumns" :key="c.key">{{ c.label }}</td>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, i) in form.items" :key="i">
            <td v-for="c in connectionColumns" :key="c.key">{{ c.value(item) || '' }}</td>
          </tr>
        </tbody>
      </table>
    </section>
  </div>
</template>

<script>
import { renderPDF } from '@/api/pdf.js'
import { bendersBlob, dialBlob } from '@/blobs.js'
import { computeInletArrows } from '@/dialArrows.js'
import { customerFields, wellFields, connectionColumns, inPairs } from '@/formFields.js'

export default {
  name: 'PDF',
  props: {
    customer: Object,
    common: Object,
    forms: Array,
  },
  data: () => ({
    bendersBlob,
    dialBlob,
    wellFields,
    connectionColumns,
    customerRows: inPairs(customerFields),
  }),
  computed: {
    // customerFields hämtar ur { customer, common } – samma form som store.
    source() {
      return { customer: this.customer, common: this.common }
    },
  },
  methods: {
    // En pil per inlopp med ifylld vinkel (geometri i @/dialArrows.js).
    inletArrows(form) {
      return computeInletArrows(form.items)
    },
    async generatePdf() {
      const body = this.$refs?.pdfRef?.innerHTML

      let cssRules = []
      for (const cssSource of document.styleSheets) {
        try {
          cssRules.push(...cssSource.rules)
        } catch {
          continue
        }
      }
      const html = `
        <!DOCTYPE html>
        <head>
          <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;700;800&display=swap" rel="stylesheet">
          <style>${cssRules.map((r) => r.cssText).join('\n')}</style>
        </head>
        <body id="pdf-content">
          ${body}
        </body>
      `
      if (!html) {
        console.error('PDF-innehållet kunde inte läsas', html)
        return
      }

      const pdf = await renderPDF(html, `bestallning.pdf`)
      if (!pdf) {
        console.error('Failed to generate PDF', pdf)
        return
      }

      const url = window.URL.createObjectURL(new Blob([pdf], { type: 'application/pdf' }))
      window.open(url, '_blank')
    },
  },
}
</script>

<style>
@media print {
  @page {
    size: A4 portrait;
    margin-left: 0;
    margin-right: 0;
    padding-left: 0;
    padding-right: 0;
  }
  @page :first {
    margin-top: 0;
    padding-top: 0;
  }
}

#pdf-content {
  padding: 2rem;
  font-family: 'Inter', Arial, sans-serif;
  font-size: 14px;
  color: #58595b;
  max-width: 800px;
  transform: scale(0.95);
}

#pdf-content a {
  color: #58595b;
  font-weight: 600;
}

.pdf-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid #58595b;
  padding-bottom: 0.5rem;
  margin-bottom: 2rem;
}

.logo {
  width: 140px;
  margin-right: 1rem;
  margin-left: 1rem;
}

.pdf-header h1 {
  color: #58595b;
  font-size: 18pt;
  margin: 0;
  margin-top: 1rem;
  text-align: left;
}

.pdf-header .subtitle {
  color: #58595b;
  font-size: 7pt;
}

.customer-info {
  width: 100%;
  font-size: 10pt;
  padding: 0 0.5rem;
  table-layout: fixed;
}

.customer-info td {
  padding: 0.1rem 0.4rem;
}
.customer-info td strong {
  float: left;
}
.customer-info td span {
  float: right;
}

#pdf-content .information {
  margin: 0.1rem 0.4rem;
}

.customer-info p,
.brunn-section p {
  margin: 0.25rem 0;
}

.brunn-section {
  border-top: 2px solid #ccc;
  padding: 0 0.5rem;
  padding-top: 1rem;
  margin-top: 2rem;
}

.brunn-section h2 {
  color: #58595b;
  font-weight: bold;
}

#dialCell {
  height: 100%;
  text-align: center;
}

/* Wrappern är 20 SVG-enheter (14.1px) större än urtavlebilden åt varje håll, och bilden
   ligger centrerad inuti. Skälet: PDF-renderaren klipper varje elementbox mot dess egen
   ram och hedrar inte overflow: visible – låg bilden kant i kant med wrappern kapades
   nummerbrickorna som ritas utanför urtavlan (se .dial-pdf-overlay nedan). */
#dialCell .dial-pdf-wrapper {
  position: relative;
  width: 226.9px;
  height: 228.3px;
  margin: -4rem auto -1rem auto;
}

#dialCell .dial-pdf-wrapper img {
  position: absolute;
  top: 14.1px;
  left: 14.1px;
  height: 200px;
  width: auto;
}

/* Overlayen täcker hela wrappern och har en viewBox med 20 enheters marginal runt
   urtavlan (se markupen ovan), så brickorna vid 90/180/270° ritas innanför ramen i
   stället för utanför. Elementets storlek måste matcha viewBoxen exakt, annars glider
   pilarna ur läge mot bakgrundsbilden. Skalan är låst av preserveAspectRatio till
   s = 200/283 = 0.70671 px per enhet:
     width  = 321 × s = 226.9
     height = 323 × s = 228.3
   Bildens offset i wrappern (14.1px) är samma marginal: 20 × s.                */
#dialCell .dial-pdf-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 226.9px;
  height: 228.3px;
  pointer-events: none;
  overflow: visible; /* behålls för webbläsaren; PDF-renderaren respekterar den inte */
}

/* Inloppspilarnas färger ligger som presentationsattribut i markupen ovan, inte här.
   Den externa PDF-renderaren applicerar dokumentets CSS på HTML-element men kaskaderar
   den inte in i inline-SVG – CSS-regler för .inlet-* ignorerades därför helt. */

.brunn-section .anslutningar {
  margin-top: 1rem;
  font-size: 9pt;
  table-layout: auto;
}
/* Rubrikerna versaliseras här, inte i texten – etiketterna delas med
   sammanfattningsvyerna via @/formFields.js. */
.brunn-section .anslutningar thead {
  color: #58595b;
  font-weight: bold;
  text-transform: uppercase;
}
.brunn-section table {
  width: 100%;
  table-layout: fixed;
}
.brunn-section td {
  padding: 0.1rem 0.4rem;
}
.brunn-section td span {
  float: right;
}

ul {
  padding-left: 1.2rem;
}
</style>
